import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { astrologyService } from '../services/astrologyService';
import './Palmistry.css';

const Palmistry: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>('heart');
  const [isDrag, setIsDrag] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    } else {
      setError('Please select a valid image file');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleAnalyze = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysisResult(null);
    setActiveTab('heart');

    try {
      const result = await astrologyService.analyzePalm(selectedFile);
      if (result.success || result.status === 'success') {
        setAnalysisResult(result);
      } else {
        setError(result.error || result.detail || 'Failed to analyze palm image');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error connecting to astrology server.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setSelectedFile(null);
    setPreview('');
    setError('');
    setAnalysisResult(null);
    setActiveTab('heart');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openPicker = () => {
    fileInputRef.current?.click();
  };

  const lineTabs = [
    { id: 'heart', label: 'Heart', color: '#f0899c' },
    { id: 'head', label: 'Head', color: '#9c8fff' },
    { id: 'life', label: 'Life', color: '#5fd0c4' },
    { id: 'fate', label: 'Fate', color: '#e3b873' }
  ];

  const getLineText = (lineId: string) => {
    if (!analysisResult || !analysisResult.sections) return '';
    const nameMap: Record<string, string> = {
      'heart': 'Heart Line',
      'head': 'Head Line',
      'life': 'Life Line',
      'fate': 'Fate Line'
    };
    return analysisResult.sections[nameMap[lineId]] || '';
  };

  const activeColor = lineTabs.find(t => t.id === activeTab)?.color || '#f0899c';

  return (
    <div className="palmistry-page">
      {!analysisResult ? (
        <>
          <header className="hero">
        <div className="wrap hero-inner">
          <div className="eyebrow">Optical palm mapping</div>
          <h1>Your palm, read like <em>a chart.</em></h1>
          <p className="lede">Upload one photo of your open palm. The model locates your heart, head, life, and fate lines, then reads each on its own — length, depth, and curve — the way a chart reads a sky full of position and timing.</p>
        </div>

        <div className="wrap upload-section">
          <div className="upload-grid">
            <div className="card">
              <div 
                className={`dropzone ${isDrag ? 'drag' : ''} ${preview ? 'filled' : ''}`} 
                tabIndex={0} 
                role="button" 
                aria-label="Upload a palm photo"
                onClick={(e) => { if ((e.target as HTMLElement).tagName !== 'BUTTON') openPicker(); }}
                onDragEnter={(e) => { e.preventDefault(); setIsDrag(true); }}
                onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDrag(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDrag(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFile(file);
                }}
              >
                <div className="dz-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"/>
                  </svg>
                </div>
                <div>
                  <div className="dz-title">Upload a palm photo</div>
                  <div className="dz-sub">Flat lighting, palm fully open, fingers relaxed. JPG or PNG, one hand at a time.</div>
                </div>
                
                {preview ? (
                  <div className="preview-box">
                    <img src={preview} alt="Palm preview" />
                    <span>{selectedFile?.name}</span>
                  </div>
                ) : (
                  <div className={`file-chip ${selectedFile ? 'show' : ''}`}>
                    <span className="dot" aria-hidden="true"></span>
                    <span>{selectedFile?.name || 'palm_photo.jpg'}</span>
                  </div>
                )}
                
                <div className="dz-actions">
                  {!selectedFile ? (
                    <button className="btn btn-primary" onClick={openPicker}>
                      Upload photo
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-primary" disabled={loading} onClick={handleAnalyze}>
                        {loading ? 'Analyzing…' : 'Analyze palm'}
                      </button>
                      <button className="btn btn-secondary" onClick={handleReset}>Reset</button>
                    </>
                  )}
                </div>
                <input type="file" ref={fileInputRef} accept="image/png, image/jpeg" style={{display: 'none'}} aria-hidden="true" onChange={handleFileSelect} />
                {error && <div style={{color: 'var(--error)', marginTop: '8px', fontSize: '14px'}}>{error}</div>}
              </div>
            </div>

            <div className="card">
              <div className="eyebrow" style={{marginBottom: 'var(--space-4)'}}>Before you scan</div>
              <ul className="checklist">
                <li>
                  <div className="check-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 11V6a2 2 0 00-4 0v5M14 10V4a2 2 0 00-4 0v6M10 10.5V6a2 2 0 00-4 0v8c0 4 2 8 7 8s7-3 7-7v-5a2 2 0 00-4 0v3"/></svg>
                  </div>
                  <p><strong>Use your dominant hand.</strong> It reads your lived path; the other reads where you started.</p>
                </li>
                <li>
                  <div className="check-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
                  </div>
                  <p><strong>Natural light beats flash.</strong> Flash flattens the shadow that gives lines contrast.</p>
                </li>
                <li>
                  <div className="check-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></svg>
                  </div>
                  <p><strong>Used, not stored.</strong> Your photo generates the reading below and isn't kept by default.</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="wrap how-section">
          <div className="how-head">
            <div>
              <div className="eyebrow">How the scan works</div>
              <h2>Three passes, one reading.</h2>
            </div>
            <div className="how-meta">~30 seconds<br/>photo to reading</div>
          </div>
          <div className="thread">
            <div className="step">
              <div className="step-num">01</div>
              <h3>Upload or capture</h3>
              <p>One clear photo of your open palm, in natural light, is all the model needs to start.</p>
            </div>
            <div className="step">
              <div className="step-num">02</div>
              <h3>Lines are mapped</h3>
              <p>The model traces your heart, head, life, and fate lines, measuring length, depth, and curve.</p>
            </div>
            <div className="step">
              <div className="step-num">03</div>
              <h3>Each line, read on its own</h3>
              <p>You get a separate reading per line, plus how they relate to your current chart.</p>
            </div>
          </div>
        </section>
      </main>
      </>
      ) : (
        <main>
          <section className="wrap" style={{ paddingTop: 'var(--space-6)' }}>
            <button className="btn btn-secondary" onClick={handleReset} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 19l-7-7 7-7"/></svg>
              Scan Another Palm
            </button>
          </section>
          {/* SUMMARY */}
          <section className="wrap reading-section pulse" id="readingSection" style={{ marginTop: 'var(--space-4)' }}>
              <div className="how-head">
                <div>
                  <div className="eyebrow">Reading results</div>
                  <h2>Your unique patterns, decoded.</h2>
                </div>
              </div>

              <div className="summary-banner">
                {analysisResult.sections && analysisResult.sections["🌟 Reading Summary"] ? (
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {analysisResult.sections["🌟 Reading Summary"]}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {analysisResult.analysis || JSON.stringify(analysisResult)}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </section>

            {/* INTERACTIVE MAPPING */}
            {analysisResult.sections && (
              <section className="wrap breakdown-section">
                <div className="how-head">
                  <div>
                    <div className="eyebrow">Interactive mapping</div>
                    <h2>Line by line breakdown.</h2>
                  </div>
                  <div className="how-meta">select a line<br/>to trace it below</div>
                </div>

                <div className="breakdown-grid">
                  <div className="palm-card">
                    <svg className="palm-svg" viewBox="0 0 240 320" fill="none" aria-hidden="true">
                      {/* outline */}
                      <rect className="palm-outline" x="55" y="150" width="130" height="140" rx="34"/>
                      <rect className="palm-outline" x="65" y="70" width="24" height="90" rx="12"/>
                      <rect className="palm-outline" x="95" y="45" width="24" height="115" rx="12"/>
                      <rect className="palm-outline" x="125" y="60" width="24" height="100" rx="12"/>
                      <rect className="palm-outline" x="153" y="85" width="20" height="75" rx="10"/>
                      <path className="palm-outline" d="M58,185 C33,179 16,158 21,136 C33,148 47,168 63,205 Z"/>

                      {/* lines */}
                      <path id="line-heart" className="palm-line" d="M70,175 C100,164 140,167 175,178" stroke="#f0899c" strokeWidth="3" opacity={activeTab === 'heart' ? 1 : 0.3}/>
                      <path id="line-head"  className="palm-line" d="M65,201 C100,207 145,201 178,194" stroke="#9c8fff" strokeWidth="3" opacity={activeTab === 'head' ? 1 : 0.3}/>
                      <path id="line-life"  className="palm-line" d="M76,160 C54,190 54,232 77,272" stroke="#5fd0c4" strokeWidth="3" opacity={activeTab === 'life' ? 1 : 0.3}/>
                      <path id="line-fate"  className="palm-line" d="M121,286 C118,240 122,196 125,156" stroke="#e3b873" strokeWidth="3" opacity={activeTab === 'fate' ? 1 : 0.3}/>

                      {/* star nodes */}
                      <g id="nodes-heart" className="star-node" opacity={activeTab === 'heart' ? 1 : 0}><circle cx="100" cy="167" r="2.6" fill="#f0899c"/><circle cx="150" cy="171" r="2.6" fill="#f0899c"/></g>
                      <g id="nodes-head" className="star-node" opacity={activeTab === 'head' ? 1 : 0}><circle cx="100" cy="206" r="2.6" fill="#9c8fff"/><circle cx="150" cy="199" r="2.6" fill="#9c8fff"/></g>
                      <g id="nodes-life" className="star-node" opacity={activeTab === 'life' ? 1 : 0}><circle cx="58" cy="200" r="2.6" fill="#5fd0c4"/><circle cx="68" cy="250" r="2.6" fill="#5fd0c4"/></g>
                      <g id="nodes-fate" className="star-node" opacity={activeTab === 'fate' ? 1 : 0}><circle cx="120" cy="240" r="2.6" fill="#e3b873"/><circle cx="124" cy="180" r="2.6" fill="#e3b873"/></g>

                      {/* constellation extensions toward panel */}
                      <path id="ext-heart" className="thread-ext" d="M175,178 L225,150" stroke="#f0899c" opacity={activeTab === 'heart' ? 1 : 0}/>
                      <path id="ext-head"  className="thread-ext" d="M178,194 L225,205" stroke="#9c8fff" opacity={activeTab === 'head' ? 1 : 0}/>
                      <path id="ext-life"  className="thread-ext" d="M77,272 L20,300" stroke="#5fd0c4" opacity={activeTab === 'life' ? 1 : 0}/>
                      <path id="ext-fate"  className="thread-ext" d="M125,156 L125,30" stroke="#e3b873" opacity={activeTab === 'fate' ? 1 : 0}/>
                    </svg>

                    <div className="tabbar" role="tablist" aria-label="Palm lines">
                      {lineTabs.map(tab => (
                        <button 
                          key={tab.id}
                          className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                          style={{ '--tab-color': tab.color } as React.CSSProperties}
                          onClick={() => setActiveTab(tab.id)}
                          role="tab" 
                          aria-selected={activeTab === tab.id}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="detail-card" style={{ '--dot-color': activeColor } as React.CSSProperties}>
                    <div className="detail-head">
                      <span className="line-dot" aria-hidden="true"></span>
                      <span className="detail-label">{lineTabs.find(t => t.id === activeTab)?.label} line</span>
                    </div>
                    <div className="detail-text markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {getLineText(activeTab) || "Analysis specific to this line."}
                      </ReactMarkdown>
                    </div>
                    
                    {analysisResult.confidence && (
                      <div className="confidence-row">
                        <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true">
                          <circle className="dial-ring-bg" cx="36" cy="36" r="30"/>
                          <circle className="dial-ring" cx="36" cy="36" r="30" transform="rotate(-90 36 36)" style={{ strokeDasharray: 188.5, strokeDashoffset: 188.5 - (188.5 * analysisResult.confidence) }}/>
                          <text className="dial-num" x="36" y="41" textAnchor="middle">{Math.round(analysisResult.confidence * 100)}%</text>
                        </svg>
                        <div>
                          <div className="dial-label">Reading confidence</div>
                          <div className="dial-note">How clearly this line could be traced in your photo.</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* TRAITS */}
            {analysisResult.sections && (
              <section className="wrap traits-section">
                <div className="how-head">
                  <div>
                    <div className="eyebrow">Deeper Synthesis</div>
                    <h2>Personality & life traits.</h2>
                  </div>
                </div>

                <div className="trait-grid">
                  {[
                    { key: 'Personality Traits', color: 'var(--head)' },
                    { key: 'Emotional Life & Relationships', color: 'var(--heart)' },
                    { key: 'Career Tendencies', color: 'var(--fate)' },
                    { key: 'Health Tendencies', color: 'var(--life)' },
                    { key: 'Life Path Themes', color: 'var(--fate)' }
                  ].map((trait, idx) => {
                    const content = analysisResult.sections[trait.key];
                    if (!content) return null;
                    return (
                      <div key={idx} className="trait-card" style={{ '--accent': trait.color } as React.CSSProperties}>
                        <div className="trait-label">{trait.key}</div>
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {typeof content === 'string' ? content : JSON.stringify(content)}
                          </ReactMarkdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
        </main>
      )}
    </div>
  );
};

export default Palmistry;
